import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { parseApiError } from '@saas-kit/lib';
import {
  Drawer,
  ErrorBanner,
  InputField,
  SubmitButton,
  toast,
} from '@saas-kit/ui';

import type { CreateTenantPayload } from './api';
import {
  EMPTY_OWNER,
  TenantOwnerFields,
  type OwnerFormState,
} from './TenantOwnerFields';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isPending: boolean;
  onSubmit: (payload: CreateTenantPayload) => Promise<void>;
}

/** Derive a URL-safe slug from a free-text workspace name. */
function slugify(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** Build the create payload for an owner section, or return an error key. */
function ownerPayload(
  owner: OwnerFormState,
): { payload: Partial<CreateTenantPayload> } | { errorKey: string } {
  if (owner.mode === 'existing') {
    if (!owner.userId.trim()) {
      return { errorKey: 'create.errors.owner_user_id_required' };
    }
    return { payload: { owner_mode: 'existing', owner_user_id: owner.userId.trim() } };
  }
  if (!owner.email.trim()) {
    return { errorKey: 'create.errors.owner_email_required' };
  }
  if (owner.mode === 'invite') {
    return { payload: { owner_mode: 'invite', owner_email: owner.email.trim() } };
  }
  if (owner.password.length < 8) {
    return { errorKey: 'create.errors.owner_password_required' };
  }
  return {
    payload: {
      owner_mode: 'new',
      owner_email: owner.email.trim(),
      owner_password: owner.password,
      ...(owner.fullName.trim()
        ? { owner_full_name: owner.fullName.trim() }
        : {}),
    },
  };
}

export function TenantCreateForm({
  open,
  onOpenChange,
  isPending,
  onSubmit,
}: Props): JSX.Element {
  const { t } = useTranslation('adminTenants');
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  // The slug tracks the name until the admin edits it by hand.
  const [slugTouched, setSlugTouched] = useState(false);
  const [owner, setOwner] = useState<OwnerFormState>(EMPTY_OWNER);
  const [error, setError] = useState<string | null>(null);

  function reset(): void {
    setName('');
    setSlug('');
    setSlugTouched(false);
    setOwner(EMPTY_OWNER);
    setError(null);
  }

  function handleClose(next: boolean): void {
    if (!next) reset();
    onOpenChange(next);
  }

  function handleNameChange(value: string): void {
    setName(value);
    if (!slugTouched) setSlug(slugify(value));
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setError(null);

    const result = ownerPayload(owner);
    if ('errorKey' in result) {
      setError(t(result.errorKey));
      return;
    }
    const payload: CreateTenantPayload = {
      name: name.trim(),
      slug: slug.trim(),
      ...result.payload,
    } as CreateTenantPayload;

    void (async () => {
      try {
        await onSubmit(payload);
        toast.success(t('toasts.created'));
        reset();
        onOpenChange(false);
      } catch (err) {
        setError(parseApiError(err).message);
      }
    })();
  }

  return (
    <Drawer
      open={open}
      onOpenChange={handleClose}
      title={t('create.title')}
      description={t('create.description')}
    >
      <form className="space-y-4" onSubmit={handleSubmit}>
        <InputField
          label={t('create.name_label')}
          value={name}
          onChange={(e) => handleNameChange(e.target.value)}
          autoFocus
          required
        />
        <InputField
          label={t('create.slug_label')}
          hint={t('create.slug_hint')}
          value={slug}
          onChange={(e) => {
            setSlug(e.target.value);
            setSlugTouched(true);
          }}
          required
        />

        <TenantOwnerFields value={owner} onChange={setOwner} />

        {error ? <ErrorBanner>{error}</ErrorBanner> : null}

        <div className="pt-2">
          <SubmitButton
            loading={isPending}
            label={t('create.submit')}
            loadingLabel={t('create.submitting')}
          />
        </div>
      </form>
    </Drawer>
  );
}
